import { CartAddEvent, CartErrorEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

const SUBSCRIBE_OPTIONS_MOBILE_QUERY = '(max-width: 749px)';
const SUBSCRIBE_OPTIONS_DESKTOP_QUERY = '(min-width: 750px)';
const DESKTOP_PANEL_CLOSE_MS = 480;
/** Matches `subscribe-options-desktop-in`'s duration. */
const DESKTOP_PANEL_OPEN_MS = 320;
/** Matches the `top: calc(100% + 4px)` gap on `.subscribe-options__custom-select-dropdown`. */
const DROPDOWN_OFFSET = 4;
const FREQUENCY_ARROW_SVG =
  '<svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg" class="subscribe-options__frequency-arrow" aria-hidden="true"><path d="M6.6001 0.599976L3.6001 3.59998L0.600098 0.599976" stroke="#181818" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * @returns {boolean}
 */
function isSubscribeMobileView() {
  return window.matchMedia(SUBSCRIBE_OPTIONS_MOBILE_QUERY).matches;
}

/**
 * @returns {boolean}
 */
function isSubscribeDesktopView() {
  return window.matchMedia(SUBSCRIBE_OPTIONS_DESKTOP_QUERY).matches;
}

/** @type {Element | null} */
let floatingFrequencyDropdownSelect = null;

/**
 * Keeps the viewport-anchored dropdown glued to its trigger while the page or
 * the carousel it lives in scrolls.
 *
 * Offsets are corrected against the box the browser actually painted rather
 * than assumed to be viewport-relative, because container queries on the
 * carousel hosts make those ancestors the containing block for fixed children.
 */
function positionFloatingFrequencyDropdown() {
  const display = floatingFrequencyDropdownSelect?.querySelector('.subscribe-options__custom-select-display');
  const dropdown = floatingFrequencyDropdownSelect?.querySelector('.subscribe-options__custom-select-dropdown');
  if (!(display instanceof HTMLElement) || !(dropdown instanceof HTMLElement)) return;

  dropdown.style.setProperty('--subscribe-dropdown-top', '0px');
  dropdown.style.setProperty('--subscribe-dropdown-left', '0px');

  const origin = dropdown.getBoundingClientRect();
  const trigger = display.getBoundingClientRect();

  dropdown.style.setProperty('--subscribe-dropdown-top', `${trigger.bottom + DROPDOWN_OFFSET - origin.top}px`);
  dropdown.style.setProperty('--subscribe-dropdown-left', `${trigger.left - origin.left}px`);
}

/**
 * @param {Element} customSelect
 */
function expandFrequencyDropdown(customSelect) {
  customSelect.classList.add('is-open');
  customSelect.querySelector('.subscribe-options__custom-select-display')?.setAttribute('aria-expanded', 'true');

  const dropdown = customSelect.querySelector('.subscribe-options__custom-select-dropdown');
  if (!customSelect.closest('[data-subscribe-options-panel]') || !(dropdown instanceof HTMLElement)) return;

  floatingFrequencyDropdownSelect = customSelect;
  dropdown.classList.add('subscribe-options__custom-select-dropdown--floating');
  positionFloatingFrequencyDropdown();

  window.addEventListener('scroll', positionFloatingFrequencyDropdown, true);
  window.addEventListener('resize', positionFloatingFrequencyDropdown);
}

/**
 * @param {Element} customSelect
 */
function collapseFrequencyDropdown(customSelect) {
  customSelect.classList.remove('is-open');
  customSelect.querySelector('.subscribe-options__custom-select-display')?.setAttribute('aria-expanded', 'false');

  const dropdown = customSelect.querySelector('.subscribe-options__custom-select-dropdown');
  if (dropdown instanceof HTMLElement) {
    dropdown.classList.remove('subscribe-options__custom-select-dropdown--floating');
    dropdown.style.removeProperty('--subscribe-dropdown-top');
    dropdown.style.removeProperty('--subscribe-dropdown-left');
  }

  if (floatingFrequencyDropdownSelect !== customSelect) return;

  floatingFrequencyDropdownSelect = null;
  window.removeEventListener('scroll', positionFloatingFrequencyDropdown, true);
  window.removeEventListener('resize', positionFloatingFrequencyDropdown);
}

/**
 * @param {ParentNode} container
 */
function collapseFrequencyDropdowns(container) {
  for (const customSelect of container.querySelectorAll('.subscribe-options__custom-select.is-open')) {
    collapseFrequencyDropdown(customSelect);
  }
}

/**
 * @param {HTMLElement} card
 */
function openDesktopSubscribePanel(card) {
  const panel = card.querySelector('[data-subscribe-options-panel]');
  if (!(panel instanceof HTMLElement)) return;

  closeAllDesktopSubscribePanels();

  card.classList.add('collection-product-card--subscribe-open', 'subscribe-options-card--open');
  panel.hidden = false;
  panel.classList.remove('is-settled');
  panel.classList.add('is-open');

  const settle = () => panel.classList.add('is-settled');
  panel.addEventListener('animationend', settle, { once: true });
  // Reduced-motion setups can skip the animation entirely, so never wait on it alone.
  window.setTimeout(settle, DESKTOP_PANEL_OPEN_MS);

  const trigger = card.querySelector('[data-subscribe-options-trigger]');
  if (trigger instanceof HTMLElement) {
    trigger.setAttribute('aria-expanded', 'true');
  }
}

/**
 * @param {HTMLElement} currentCard
 */
function applyDesktopSubscribePanelClosedState(currentCard) {
  const shouldRevealButtons = isSubscribeDesktopView() && currentCard.matches(':hover');
  const buttonsWrapper = currentCard.querySelector(
    '.custom-grid-card__buttons-wrapper.product-grid-item-quick-add'
  );

  if (shouldRevealButtons && buttonsWrapper instanceof HTMLElement) {
    buttonsWrapper.classList.add('custom-grid-card__buttons-wrapper--reveal-reset');
    buttonsWrapper.getBoundingClientRect();
  }

  currentCard.classList.remove('collection-product-card--subscribe-open', 'subscribe-options-card--open');

  const trigger = currentCard.querySelector('[data-subscribe-options-trigger]');
  if (trigger instanceof HTMLElement) {
    trigger.setAttribute('aria-expanded', 'false');
  }

  if (shouldRevealButtons && buttonsWrapper instanceof HTMLElement) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        buttonsWrapper.classList.remove('custom-grid-card__buttons-wrapper--reveal-reset');
      });
    });
  }
}

/**
 * @param {HTMLElement} panel
 */
function hideDesktopSubscribePanel(panel) {
  panel.classList.remove('is-open', 'is-settled', 'is-closing', 'is-closing-active');
  panel.hidden = true;
}

/**
 * @param {HTMLElement} panel
 * @returns {Promise<void>}
 */
function waitForDesktopPanelCloseAnimation(panel) {
  return new Promise((resolve) => {
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      resolve();
    };

    /** @param {TransitionEvent} event */
    const onTransitionEnd = (event) => {
      if (event.target !== panel) return;
      if (event.propertyName === 'opacity' || event.propertyName === 'transform') {
        finish();
      }
    };

    panel.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(finish, DESKTOP_PANEL_CLOSE_MS + 100);
  });
}

/**
 * @param {HTMLElement} panel
 */
function startDesktopPanelCloseAnimation(panel) {
  panel.classList.remove('is-open', 'is-settled');
  panel.classList.add('is-closing');
  panel.classList.remove('is-closing-active');
  panel.getBoundingClientRect();

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      panel.classList.add('is-closing-active');
    });
  });
}

/**
 * @param {HTMLElement} [card]
 * @param {{ animated?: boolean }} [options]
 * @returns {Promise<void>}
 */
async function closeDesktopSubscribePanel(card, options = {}) {
  const { animated = false } = options;
  const cards = card
    ? [card]
    : Array.from(document.querySelectorAll('[data-subscribe-options-card].collection-product-card--subscribe-open, [data-subscribe-options-card].subscribe-options-card--open'));

  await Promise.all(
    cards.map(async (currentCard) => {
      if (!(currentCard instanceof HTMLElement)) return;

      const panel = currentCard.querySelector('[data-subscribe-options-panel]');
      if (!(panel instanceof HTMLElement)) {
        applyDesktopSubscribePanelClosedState(currentCard);
        return;
      }

      if (panel.classList.contains('is-closing')) return;

      collapseFrequencyDropdowns(panel);

      const shouldAnimate =
        animated && isSubscribeDesktopView() && panel.classList.contains('is-open') && !panel.hidden;

      if (shouldAnimate) {
        startDesktopPanelCloseAnimation(panel);
        await waitForDesktopPanelCloseAnimation(panel);
        hideDesktopSubscribePanel(panel);
        applyDesktopSubscribePanelClosedState(currentCard);
        return;
      }

      applyDesktopSubscribePanelClosedState(currentCard);
      hideDesktopSubscribePanel(panel);
    })
  );
}

/**
 * @param {{ animated?: boolean }} [options]
 * @returns {Promise<void>}
 */
function closeAllDesktopSubscribePanels(options = {}) {
  return closeDesktopSubscribePanel(undefined, { animated: options.animated ?? false });
}

/**
 * Closes mobile dialog and desktop panel.
 */
function closeAllSubscribeOptions() {
  closeAllDesktopSubscribePanels();
  forceCloseAllSubscribeOptionsDialogs();
}

/**
 * Animates the subscribe sheet closed, then hard-hides it.
 * Resolves only after the slide/fade-out finishes (or a short timeout).
 *
 * @returns {Promise<void>}
 */
async function closeSubscribeOptionsDialogsWithAnimation() {
  const dialogs = Array.from(document.querySelectorAll('dialog.subscribe-options__dialog')).filter(
    (dialog) => dialog instanceof HTMLDialogElement && (dialog.open || dialog.hasAttribute('open'))
  );

  if (dialogs.length === 0) {
    forceCloseAllSubscribeOptionsDialogs();
    return;
  }

  await Promise.all(
    dialogs.map(
      (dialog) =>
        new Promise((resolve) => {
          if (!(dialog instanceof HTMLDialogElement)) {
            resolve();
            return;
          }

          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            dialog.removeEventListener('animationend', onAnimationEnd);
            resolve();
          };

          const onAnimationEnd = (event) => {
            if (event.target !== dialog) return;
            finish();
          };

          dialog.classList.add('dialog-closing');
          dialog.addEventListener('animationend', onAnimationEnd);
          window.setTimeout(finish, 280);
        })
    )
  );

  forceCloseAllSubscribeOptionsDialogs();
}

/**
 * Hard-hides every subscribe bottom sheet (no animation).
 * Used after the close animation, or as a fallback.
 */
function forceCloseAllSubscribeOptionsDialogs() {
  document.querySelectorAll('dialog.subscribe-options__dialog').forEach((dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return;

    dialog.classList.remove('dialog-closing');

    try {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    } catch (_) {
      // ignore
    }

    dialog.removeAttribute('open');
    dialog.hidden = true;
    dialog.style.cssText =
      'display:none!important;visibility:hidden!important;opacity:0!important;pointer-events:none!important;';

    const host = dialog.closest('dialog-component');
    if (host instanceof HTMLElement) {
      host.style.setProperty('display', 'none', 'important');
    }
  });

  document.documentElement.removeAttribute('scroll-lock');
}

/**
 * Clears forced-hide styles so the sheet can open again next time.
 *
 * @param {HTMLDialogElement | null} [dialog]
 */
function prepareSubscribeDialogForOpen(dialog) {
  if (!(dialog instanceof HTMLDialogElement)) return;

  dialog.hidden = false;
  dialog.style.cssText = '';
  dialog.classList.remove('dialog-closing');

  const host = dialog.closest('dialog-component');
  if (host instanceof HTMLElement) {
    host.style.removeProperty('display');
  }
}

/** @type {Map<string, Promise<Document>>} */
const productPageCache = new Map();

/**
 * @param {string} productUrl
 * @param {string} variantId
 * @returns {Promise<Document>}
 */
async function fetchProductPage(productUrl, variantId) {
  const url = new URL(productUrl, window.location.origin);
  url.searchParams.set('variant', variantId);
  const cacheKey = url.toString();
  const cachedPage = productPageCache.get(cacheKey);

  if (cachedPage) return cachedPage;

  const request = (async () => {
    const response = await fetch(cacheKey);
    if (!response.ok) throw new Error(`Product page fetch failed: ${response.status}`);

    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  })();

  productPageCache.set(cacheKey, request);

  try {
    return await request;
  } catch (error) {
    productPageCache.delete(cacheKey);
    throw error;
  }
}

/**
 * Reads the PDP subscription gift configuration so collection quick-add matches
 * the product-page add-to-cart behavior.
 *
 * @param {HTMLFormElement} form
 * @returns {Promise<string[]>}
 */
async function getSubscriptionGiftVariantIds(form) {
  const productUrl = form.dataset.productUrl;
  const variantInput = form.querySelector('[name="id"]');
  const sellingPlanInput = form.querySelector('[name="selling_plan"]');
  const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
  const sellingPlan = sellingPlanInput instanceof HTMLInputElement ? sellingPlanInput.value : '';

  if (!productUrl || !variantId || !sellingPlan) return [];

  const productPage = await fetchProductPage(productUrl, variantId);
  const subscriptionWidget = productPage.querySelector('pdp-subscription-gifts');
  if (subscriptionWidget?.getAttribute('data-disable-gift-auto-add') === 'true') return [];

  const selectedPlanName = (form.dataset.sellingPlanName ?? '').toLowerCase();
  const giftVariantIds = new Set();

  for (const gift of productPage.querySelectorAll(
    '[data-pdp-subscription-gift][data-variant-id]'
  )) {
    const giftVariantId = gift.getAttribute('data-variant-id');
    if (!giftVariantId || giftVariantId === variantId) continue;

    const unavailablePlans = (gift.getAttribute('data-unavailable-plans') ?? '')
      .split(',')
      .map((plan) => plan.trim().toLowerCase())
      .filter(Boolean);
    const isUnavailable = unavailablePlans.some(
      (planKeyword) => selectedPlanName && selectedPlanName.includes(planKeyword)
    );

    if (!isUnavailable) giftVariantIds.add(giftVariantId);
  }

  return [...giftVariantIds];
}

/**
 * @param {HTMLFormElement} form
 * @param {string[]} giftVariantIds
 * @returns {{ items: Array<{ id: number, quantity: number, selling_plan?: number, properties?: Record<string, string> }> }}
 */
function buildSubscriptionCartPayload(form, giftVariantIds) {
  const formData = new FormData(form);
  const variantId = Number(formData.get('id'));
  const quantity = Number(formData.get('quantity') ?? 1);
  const sellingPlanId = Number(formData.get('selling_plan'));
  /** @type {Record<string, string>} */
  const properties = {};

  for (const [name, value] of formData.entries()) {
    const propertyMatch = name.match(/^properties\[(.+)\]$/);
    const propertyName = propertyMatch?.[1];

    if (propertyName && typeof value === 'string') {
      properties[propertyName] = value;
    }
  }

  const mainItem = {
    id: variantId,
    quantity,
    selling_plan: sellingPlanId,
    ...(Object.keys(properties).length > 0 ? { properties } : {}),
  };
  const giftItems = giftVariantIds.map((giftVariantId) => ({
    id: Number(giftVariantId),
    quantity: 1,
  }));

  return { items: [mainItem, ...giftItems] };
}

/**
 * @param {HTMLFormElement} form
 * @param {SubmitEvent | MouseEvent} event
 */
async function handleCollectionCardSubmit(form, event) {
  event.preventDefault();
  event.stopPropagation();
  if ('stopImmediatePropagation' in event) event.stopImmediatePropagation();

  if (form.dataset.collectionSubmitting === 'true') return;
  form.dataset.collectionSubmitting = 'true';

  const button = form.querySelector('button[type="submit"]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) {
    form.dataset.collectionSubmitting = 'false';
    return;
  }

  const originalHTML = button.innerHTML;
  const isSubscribeSheet = Boolean(
    form.classList.contains('subscribe-options__form') &&
      (form.closest('dialog.subscribe-options__dialog') || form.closest('.subscribe-options__desktop-panel'))
  );
  const isMobileSubscribeSheet = isSubscribeSheet && isSubscribeMobileView();
  const isDesktopSubscribePanel = isSubscribeSheet && isSubscribeDesktopView();

  button.textContent = 'ADDING...';
  button.disabled = true;

  const formData = new FormData(form);
  const variantId = String(formData.get('id') ?? '');
  const quantity = Number(formData.get('quantity')) || 1;

  try {
    /** @type {string[]} */
    let giftVariantIds = [];

    try {
      giftVariantIds = await getSubscriptionGiftVariantIds(form);
    } catch (error) {
      console.error('Collection subscription gift lookup failed', error);
    }

    const sellingPlanInput = form.querySelector('[name="selling_plan"]');
    const hasSubscription =
      sellingPlanInput instanceof HTMLInputElement && Boolean(sellingPlanInput.value);
    /** @type {RequestInit} */
    const requestOptions = hasSubscription
      ? {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(buildSubscriptionCartPayload(form, giftVariantIds)),
        }
      : {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body: new FormData(form),
        };
    const response = await fetch(Theme.routes.cart_add_url, {
      ...requestOptions,
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Add to cart failed');
    }

    // Plain text only — never append a checkmark / tick.
    button.textContent = 'ADDED';
    button.setAttribute('aria-label', 'Added');

    // Show ADDED, close panel/sheet, THEN open cart.
    if (isMobileSubscribeSheet) {
      await new Promise((resolve) => window.setTimeout(resolve, 500));
      await closeSubscribeOptionsDialogsWithAnimation();
    } else if (isDesktopSubscribePanel) {
      await new Promise((resolve) => window.setTimeout(resolve, 500));
      const card = form.closest('[data-subscribe-options-card]');
      if (card instanceof HTMLElement) {
        await closeDesktopSubscribePanel(card, { animated: true });
      }
    }

    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: {
        Accept: 'application/json',
      },
    });
    const cart = await cartResponse.json();

    document.dispatchEvent(
      new CartAddEvent(cart, variantId, {
        source: 'collection-product-card',
        itemCount: cart.item_count,
        productId: String(data.product_id ?? ''),
        variantId: String(data.variant_id ?? variantId),
      })
    );

    document.dispatchEvent(
      new CartUpdateEvent(cart, form.id || 'collection-product-card', {
        source: 'collection-product-card',
        itemCount: cart.item_count,
        productId: String(data.product_id ?? ''),
        variantId: String(data.variant_id ?? variantId),
      })
    );

    const cartDrawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (
      document.querySelector('cart-drawer-component')
    );
    if (typeof cartDrawer?.open === 'function') {
      cartDrawer.open();
    }
  } catch (error) {
    console.error('Collection product card add to cart error', error);
    button.textContent = 'ERROR';

    const message = error instanceof Error ? error.message : 'Add to cart failed';
    document.dispatchEvent(new CartErrorEvent(form.id || 'collection-product-card', message));

    document.dispatchEvent(
      new CartUpdateEvent({}, form.id || 'collection-product-card', {
        didError: true,
        source: 'collection-product-card',
        itemCount: quantity,
        variantId,
      })
    );
  } finally {
    window.setTimeout(() => {
      if (isMobileSubscribeSheet) {
        forceCloseAllSubscribeOptionsDialogs();
      }

      if (isDesktopSubscribePanel) {
        const card = form.closest('[data-subscribe-options-card]');
        if (card instanceof HTMLElement) {
          closeDesktopSubscribePanel(card);
        }
      }

      button.innerHTML = originalHTML;
      button.disabled = false;
      form.dataset.collectionSubmitting = 'false';
    }, 2000);
  }
}

/**
 * Normalizes selling plan option labels to short values like "1 month".
 *
 * @param {HTMLOptionElement} option
 * @returns {string}
 */
function getFrequencyOptionLabel(option) {
  const rawLabel = (option.dataset.label || option.textContent || '').trim();
  const frequencyMatch = rawLabel.match(/(\d+)\s*(day|days|week|weeks|month|months|year|years)/i);

  if (frequencyMatch) {
    return `${frequencyMatch[1]} ${frequencyMatch[2].toLowerCase()}`;
  }

  return rawLabel
    .replace(/every\s+/i, '')
    .replace(/\s*subscription\s*/gi, ' ')
    .replace(/\s+with\s+.+$/i, '')
    .replace(/\s+-\s+.+$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {HTMLSelectElement} select
 */
function handleFrequencyChange(select) {
  const option = select.selectedOptions[0];
  if (!option) return;

  const form = select.closest('.subscribe-options__form');
  if (!(form instanceof HTMLElement)) return;

  const sellingPlanInput = form.querySelector('[data-subscribe-options-selling-plan-input]');
  if (sellingPlanInput instanceof HTMLInputElement) {
    sellingPlanInput.value = option.value;
  }

  const priceDisplay = form.querySelector('[data-subscribe-options-price]');
  const price = option.dataset.price;
  if (priceDisplay && price) {
    priceDisplay.textContent = price;
  }

  const customSelect = form.querySelector('[data-subscribe-options-custom-select]');
  const customText = customSelect?.querySelector('[data-subscribe-options-custom-text]');
  if (customText) {
    customText.textContent = getFrequencyOptionLabel(option);
  }

  customSelect?.querySelectorAll('[data-subscribe-options-custom-option]').forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    item.classList.toggle('is-selected', item.dataset.value === option.value);
    item.setAttribute('aria-selected', item.dataset.value === option.value ? 'true' : 'false');
  });
}

/**
 * @param {HTMLSelectElement} select
 */
function initSubscribeFrequencyCustomSelect(select) {
  if (select.dataset.customSelectInit === 'true') return;

  const wrapper = select.closest('.subscribe-options__frequency-select-wrapper');
  const mount = wrapper?.querySelector('[data-subscribe-options-custom-select]');
  if (!(wrapper instanceof HTMLElement) || !(mount instanceof HTMLElement)) return;

  select.dataset.customSelectInit = 'true';

  const customSelect = document.createElement('div');
  customSelect.className = 'subscribe-options__custom-select';
  customSelect.dataset.subscribeOptionsCustomSelect = '';

  const display = document.createElement('button');
  display.type = 'button';
  display.className = 'subscribe-options__custom-select-display';
  display.setAttribute('aria-haspopup', 'listbox');
  display.setAttribute('aria-expanded', 'false');
  // No <label> wraps this control (see subscribe-options-widget-global.md), so give it
  // its own accessible name instead of relying on implicit label association.
  display.setAttribute('aria-label', 'Delivery frequency');

  const text = document.createElement('span');
  text.className = 'subscribe-options__custom-select-text';
  text.dataset.subscribeOptionsCustomText = '';
  text.textContent = select.selectedOptions[0] ? getFrequencyOptionLabel(select.selectedOptions[0]) : '';

  display.append(text);
  display.insertAdjacentHTML('beforeend', FREQUENCY_ARROW_SVG);

  const dropdown = document.createElement('ul');
  dropdown.className = 'subscribe-options__custom-select-dropdown';
  dropdown.setAttribute('role', 'listbox');
  dropdown.dataset.subscribeOptionsCustomDropdown = '';

  for (const option of select.options) {
    const item = document.createElement('li');
    item.className = 'subscribe-options__custom-select-option';
    item.dataset.subscribeOptionsCustomOption = '';
    item.dataset.value = option.value;
    item.setAttribute('role', 'option');
    item.textContent = getFrequencyOptionLabel(option);

    if (option.selected) {
      item.classList.add('is-selected');
      item.setAttribute('aria-selected', 'true');
    } else {
      item.setAttribute('aria-selected', 'false');
    }

    item.addEventListener('click', (event) => {
      event.stopPropagation();
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      collapseFrequencyDropdown(customSelect);
    });

    dropdown.appendChild(item);
  }

  display.addEventListener('click', (event) => {
    event.stopPropagation();

    if (customSelect.classList.contains('is-open')) {
      collapseFrequencyDropdown(customSelect);
    } else {
      expandFrequencyDropdown(customSelect);
    }
  });

  customSelect.append(display, dropdown);
  mount.replaceWith(customSelect);

  select.addEventListener('change', () => handleFrequencyChange(select));
}

/**
 * Resolves the subscribe dialog host for the clicked card.
 * Sheet ids are often duplicated across homepage / mega menu / mobile nav for the
 * same variant — never use document.getElementById alone or the first (often hidden)
 * host opens and the sheet appears not to work.
 *
 * @param {HTMLElement | null} card
 * @param {string} sheetId
 * @returns {HTMLElement | null}
 */
function getSubscribeDialogComponent(card, sheetId) {
  if (card instanceof HTMLElement) {
    const fromCard =
      (sheetId
        ? card.querySelector(`dialog-component#${CSS.escape(sheetId)}`)
        : null) ||
      card.querySelector('dialog-component.custom-grid-card__subscribe-options') ||
      card.querySelector('dialog-component');
    if (fromCard instanceof HTMLElement) return fromCard;
  }

  const fromDocument = sheetId ? document.getElementById(sheetId) : null;
  return fromDocument instanceof HTMLElement ? fromDocument : null;
}

/**
 * Opens subscribe options — bottom sheet on mobile, inline card panel on desktop.
 *
 * @param {MouseEvent} event
 * @param {string} sheetId
 */
function handleSubscribeTriggerClick(event, sheetId) {
  event.preventDefault();
  event.stopPropagation();

  const cardFromEvent =
    (event.currentTarget instanceof HTMLElement && event.currentTarget.closest('[data-subscribe-options-card]')) ||
    (event.target instanceof Element && event.target.closest('[data-subscribe-options-card]')) ||
    null;
  const card =
    cardFromEvent ||
    getSubscribeDialogComponent(null, sheetId)?.closest('[data-subscribe-options-card]') ||
    null;

  if (isSubscribeMobileView()) {
    const dialogComponent = getSubscribeDialogComponent(
      card instanceof HTMLElement ? card : null,
      sheetId
    );
    if (!dialogComponent || typeof dialogComponent.showDialog !== 'function') return;

    closeAllDesktopSubscribePanels();

    const dialog = dialogComponent.querySelector('dialog.subscribe-options__dialog');
    prepareSubscribeDialogForOpen(dialog instanceof HTMLDialogElement ? dialog : null);

    dialogComponent.showDialog();
    return;
  }

  if (isSubscribeDesktopView() && card instanceof HTMLElement) {
    forceCloseAllSubscribeOptionsDialogs();
    openDesktopSubscribePanel(card);
  }
}

/**
 * @param {HTMLElement} closeButton
 */
async function handleSubscribeCloseClick(closeButton) {
  const card = closeButton.closest('[data-subscribe-options-card]');
  const isDesktopPanelClose = closeButton.closest('[data-subscribe-options-panel]') instanceof HTMLElement;

  if (isDesktopPanelClose && isSubscribeDesktopView() && card instanceof HTMLElement) {
    await closeDesktopSubscribePanel(card, { animated: true });
    return;
  }

  if (isSubscribeMobileView()) {
    await closeSubscribeOptionsDialogsWithAnimation();
    return;
  }

  closeAllSubscribeOptions();
}

/**
 * @param {MouseEvent} event
 */
function handleDocumentSubscribeClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.closest('[data-subscribe-options-trigger], .subscribe-options__desktop-panel, .subscribe-options__dialog')) {
    return;
  }

  closeAllDesktopSubscribePanels({ animated: true });
}

/**
 * Dedicated click path for the sheet submit button so close/ADDED always runs
 * even if form submit wiring is lost after AJAX re-renders.
 *
 * @param {MouseEvent} event
 */
function handleSubscribeSubmitClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('.subscribe-options__submit');
  if (!(button instanceof HTMLButtonElement)) return;

  const form = button.closest('form.subscribe-options__form');
  if (!(form instanceof HTMLFormElement)) return;

  event.preventDefault();
  event.stopPropagation();
  handleCollectionCardSubmit(form, event);
}

function closeOpenSubscribeFrequencyDropdowns(event) {
  const target = event?.target;
  if (target instanceof Element && target.closest('.subscribe-options__custom-select')) return;

  collapseFrequencyDropdowns(document);
}

/**
 * @param {ParentNode} [root]
 */
export function initCollectionProductCards(root = document) {
  for (const form of root.querySelectorAll('[data-subscribe-options-card] .custom-grid-card__form[data-collection-ajax-cart], [data-subscribe-options-card] .subscribe-options__form[data-collection-ajax-cart]')) {
    if (!(form instanceof HTMLFormElement) || form.dataset.collectionAjaxInit === 'true') continue;

    form.dataset.collectionAjaxInit = 'true';
    form.addEventListener('submit', (event) => handleCollectionCardSubmit(form, event));
  }

  for (const select of root.querySelectorAll('[data-subscribe-options-card] [data-subscribe-options-frequency-select]')) {
    if (!(select instanceof HTMLSelectElement)) continue;
    initSubscribeFrequencyCustomSelect(select);
  }

  for (const trigger of root.querySelectorAll('[data-subscribe-options-card] [data-subscribe-options-trigger]')) {
    if (!(trigger instanceof HTMLButtonElement) || trigger.dataset.subscribeTriggerInit === 'true') continue;

    trigger.dataset.subscribeTriggerInit = 'true';
    trigger.setAttribute('aria-expanded', 'false');
    const sheetId = trigger.dataset.subscribeOptionsTrigger;
    trigger.addEventListener('click', (event) => handleSubscribeTriggerClick(event, sheetId ?? ''));
  }

  for (const closeButton of root.querySelectorAll('[data-subscribe-options-card] [data-subscribe-options-close]')) {
    if (!(closeButton instanceof HTMLButtonElement) || closeButton.dataset.subscribeCloseInit === 'true') continue;

    closeButton.dataset.subscribeCloseInit = 'true';
    closeButton.addEventListener('click', () => handleSubscribeCloseClick(closeButton));
  }
}

function scheduleInit() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => initCollectionProductCards());
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCollectionProductCards(), { once: true });
} else {
  initCollectionProductCards();
}

if (!document.body.dataset.subscribeOptionsSubmitInit) {
  document.body.dataset.subscribeOptionsSubmitInit = 'true';
  document.addEventListener('click', handleSubscribeSubmitClick, true);
  document.addEventListener('click', closeOpenSubscribeFrequencyDropdowns);
  document.addEventListener('click', handleDocumentSubscribeClick);
}

document.addEventListener(ThemeEvents.FilterUpdate, scheduleInit);
